import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import React, { useState, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import { EndPoints } from "../../../../Constants/endpoints";
import { useToast } from "../../../../Services/ToastContext";
import {
  DateInput,
  GenderRadioSelect,
  GeneralInput,
  PhoneAutocompleteInput,
  ProfileAvatar,
} from "../../../../components";
import { useAppSelector, useGetData } from "../../../../hooks";
import usePostData from "../../../../hooks/ReactQuery/usePostData";
import { PatientDetails } from "../../../../interface/GeneralResponse";
import { RootState } from "../../../../redux/store";
import { truncateText } from "../../../../utils/stringsUtils";
import {
  calculateAge,
  formatAge,
  normalizeGender,
} from "../../../../utils/utils";
import { useParams } from "react-router-dom";

interface PatientDetailsFormProps {
  onSubmit: (data: PatientDetails) => void;
  onPatientLoaded?: (data: PatientDetails) => void; // New prop
}

const AppointmentPatientDetailsForm: React.FC<PatientDetailsFormProps> = ({
  onSubmit: onSubmitProp,
  onPatientLoaded,
}) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [selectedPatient, setSelectedPatient] = useState<PatientDetails>();

  const validate = useSelector((state: RootState) => state.phValidation);

  // Fetch patient details if id exists
  const { data: patientData, isLoading: isPatientLoading } =
    useGetData<PatientDetails>(
      EndPoints.GetPatients,
      {
        enabled: !!id,
      },
      id,
    );

  // Set patient data when fetched
  useEffect(() => {
    if (patientData?.Data) {
      const patient = patientData.Data;
      setSelectedPatient(patient);
      setValue("fullName", patient.FullName || "");
      setValue("phoneNumber", patient.PhoneNumber || "");
      setValue(
        "birthDate",
        patient.Birthday
          ? moment.unix(parseInt(patient.Birthday)).format("YYYY-MM-DD")
          : "",
      );
      setValue("gender", normalizeGender(patient.Gender));

      // Notify parent component that patient is loaded
      if (onPatientLoaded) {
        onPatientLoaded(patient);
      }
    }
  }, [patientData]);

  const patientDetailsSchema = z.object(
    !selectedPatient
      ? {
          fullName: z
            .string()
            .min(4, { message: `${t("fullname_min_length_4")}` })
            .optional(),
          phoneNumber: z
            .string()
            .min(9, t("phone_number_required"))
            .superRefine((value, ctx) => {
              if (!validate) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t("validation_configuration_error"),
                });
                return;
              }

              const phoneNumber = value.replace(/[^0-9]/g, "");
              const countryStart = validate.Start.toString();
              const countryLength = validate.Length;
              const startWith0 = `0${countryStart}`;
              const startWithCountryStart = `${countryStart}`;

              if (phoneNumber.startsWith(startWith0)) {
                if (phoneNumber.length !== countryLength + 1) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("phone_number_must_be_and_start_with", {
                      length: countryLength + 1,
                      start: startWith0,
                    }),
                  });
                }
              } else if (phoneNumber.startsWith(startWithCountryStart)) {
                if (phoneNumber.length !== countryLength) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: t("phone_number_must_be_and_start_with", {
                      length: countryLength,
                      start: startWithCountryStart,
                    }),
                  });
                }
              } else {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message: t("phone_number_must_start_with", {
                    start1: startWithCountryStart,
                    start2: startWith0,
                  }),
                });
              }
            }),
          birthDate: z
            .string()
            .min(1, { message: t("birth_date_required") })
            .optional(),
          gender: z
            .string()
            .min(1, { message: t("please_select_your_gender") }),
        }
      : {},
  );

  type PatientDetailsFormData = z.infer<typeof patientDetailsSchema>;

  const methods = useForm<PatientDetailsFormData>({
    resolver: zodResolver(patientDetailsSchema),
    defaultValues: {
      fullName: "",
      phoneNumber: "",
      birthDate: "",
      gender: "",
    },
  });

  const { control, handleSubmit, setValue, reset } = methods;

  const { showToast } = useToast();

  const { mutate: createPatient } = usePostData(EndPoints.CreatePatient);
  const queryClient = useQueryClient();

  const registration = useAppSelector((state: RootState) => state.registration);
  const countryId = Number(registration.userDetails.CountryId);

  const onSubmit: SubmitHandler<PatientDetailsFormData> = (data) => {
    if (!selectedPatient) {
      // Create a new patient
      let phoneNumber = data.phoneNumber;
      if (phoneNumber?.startsWith("0")) {
        phoneNumber = phoneNumber.substring(1);
      }
      const requestData = {
        FullName: data.fullName,
        PhoneNumber: phoneNumber,
        Birthday: data.birthDate,
        Gender: data.gender,
        CountryId: countryId,
      };

      createPatient(requestData, {
        onSuccess: (response: any) => {
          showToast(t("patient_created_successfully"), "success");
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetMyPatient],
            exact: false,
          });
          onSubmitProp(response.Data);
        },
        onError: (error: any) => {
          showToast(
            error.message ? error.message : t("failed_to_create_patient"),
            "error",
          );
        },
      });
    } else {
      onSubmitProp(selectedPatient);
    }
  };

  const onOptionSelectFullName = (option: PatientDetails) => {
    setSelectedPatient(option);
    setValue("fullName", option.FullName || "");
    setValue("phoneNumber", option.PhoneNumber || "");
    setValue(
      "birthDate",
      moment.unix(parseInt(option.Birthday ?? "")).format("YYYY-MM-DD"),
    );
    setValue("gender", normalizeGender(option.Gender));
  };

  const handleClearSelection = () => {
    reset();
    setSelectedPatient(undefined);
  };

  // Show loading state while fetching patient
  if (isPatientLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <div className="text-center">
          <div className="loader"></div>
          <p className="mt-2 text-sm text-body-text">
            {t("loading_patient_details")}
          </p>
        </div>
      </div>
    );
  }

  // If id exists and patient is selected, show patient info without inputs
  if (id && selectedPatient) {
    return (
      <div className="flex h-fit items-center gap-4 rounded-lg border border-primary-click bg-white p-3">
        <ProfileAvatar
          Id={selectedPatient?.Id}
          className="h-12 w-12 rounded-full object-cover"
          src={selectedPatient?.Image}
          alt={truncateText(selectedPatient?.FullName, 20)}
          FullName={selectedPatient?.FullName}
        />
        <div className="flex flex-col justify-center gap-2">
          <div className="text-sm font-bold">
            {truncateText(selectedPatient?.FullName, 30)}
          </div>
          <div className="text-xs">
            {selectedPatient?.Birthday &&
              `${calculateAge(selectedPatient?.Birthday)} ${t("years_old")} . ${" "}`}
            {t(selectedPatient?.Gender?.trim().toLowerCase())}
          </div>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        id="patient-details-form"
        onChange={(event: any) => {
          if (!event?.target?.value) {
            handleClearSelection();
          }
        }}
        onError={(error) => {}}
        onSubmit={handleSubmit(onSubmit)}
        className="flex w-full flex-col gap-4"
      >
        {!selectedPatient ? (
          <>
            <PhoneAutocompleteInput<PatientDetails[], PatientDetails>
              name="patientSearch"
              controllerName="phoneNumber"
              control={control}
              placeholder={t("phone_number")}
              required={false}
              getEndpoint={EndPoints.SearchMyPatients}
              inputProps={{ maxLength: 60 }}
              getOptionLabel={(option: PatientDetails) => {
                let isContainStars = option.FullName?.includes("*");  
                return {
                  value: `${option.FullName}`,
                  label: (
                    <div className="flex items-center gap-4">
                      <ProfileAvatar
                        Id={option?.Id}
                        FullName={option?.FullName}
                        src={option?.Image}
                        size="small" 
                        className="h-12 w-12 rounded-full object-cover object-top"
                      />
                      <div className="flex flex-col gap-2">
                        <p className="text-sm font-bold">
                          {truncateText(option?.FullName, 30)}
                        </p>
                        {!isContainStars && (
                          <p className="text-xs">
                            {t(option?.Gender?.toLocaleLowerCase()) +
                              " • " +
                              (option?.Birthday &&
                                formatAge(option?.Birthday) +
                                  " " +
                                  t("years_old"))}
                          </p>
                        )}
                      </div>
                    </div>
                  ),
                };
              }}
              getOptionValue={(option: PatientDetails) => option.Id.toString()}
              onOptionSelect={(option: PatientDetails) => {
                onOptionSelectFullName(option);
              }}
            />
            <GeneralInput
              name="fullName"
              label={t("full_name")}
              placeholder={t("full_name")}
              required
              maxLength={60}
            />
            <DateInput
              maxDate={moment().toDate()}
              label={t("birth_date")}
              name="birthDate"
            />
            <GenderRadioSelect required name="gender" />
          </>
        ) : (
          <div className="flex h-fit justify-between gap-4 rounded-lg border border-primary-click bg-white p-3">
            <div className="flex gap-3">
              <ProfileAvatar
                Id={selectedPatient?.Id}
                className="h-12 w-12 rounded-full object-cover"
                src={selectedPatient?.Image}
                alt={truncateText(selectedPatient?.FullName, 20)}
                FullName={selectedPatient?.FullName}
                size="medium"
              />
              <div className="flex flex-col justify-center gap-2">
                <div className="text-sm font-bold">
                  {truncateText(selectedPatient?.FullName, 30)}
                </div>
                <div className="text-xs">
                  {selectedPatient?.Birthday &&
                    `${calculateAge(selectedPatient?.Birthday)} ${t("years_old")} . ${" "}`}

                  {t(selectedPatient?.Gender?.trim().toLowerCase())}
                </div>
              </div>
            </div>
            <div
              className="cursor-pointer text-2xl hover:text-error"
              onClick={() => {
                reset();
                setSelectedPatient(undefined);
              }}
            >
              &times;
            </div>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default AppointmentPatientDetailsForm;
